import {
  Content,
  Header,
  InfoCard,
  Link,
  Page,
} from '@backstage/core-components';
import {
  HomePageStarredEntities,
  HomePageToolkit,
} from '@backstage/plugin-home';
import { HomePageSearchBar } from '@backstage/plugin-search';
import {
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CategoryIcon from '@material-ui/icons/Category';
import CodeIcon from '@material-ui/icons/Code';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import SmartToyIcon from '@material-ui/icons/Android';
import { ComponentType } from 'react';
import { komaxColors } from '../../theme/komax';

const useStyles = makeStyles(theme => ({
  searchBar: {
    maxWidth: 800,
    margin: 'auto',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    borderRadius: theme.shape.borderRadius,
  },
  step: {
    height: '100%',
    borderTop: `4px solid ${komaxColors.blue}`,
  },
  stepArea: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  stepIcon: {
    display: 'block',
    color: theme.palette.primary.main,
    fontSize: 36,
    marginBottom: theme.spacing(1),
  },
  stepNumber: {
    display: 'block',
    color: komaxColors.red,
    fontWeight: 700,
  },
  why: {
    '& li': { marginBottom: theme.spacing(1) },
  },
}));

type Step = {
  title: string;
  text: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
};

// The four things a newcomer needs, in the order they need them.
const steps: Step[] = [
  {
    title: 'Find what exists',
    text: 'Browse services, APIs and libraries across GitLab and Azure DevOps — with owner and docs.',
    to: '/catalog',
    icon: CategoryIcon,
  },
  {
    title: 'Add your repository',
    text: 'Generate the catalog-info.yaml for your repo in one form. Commit it, and it shows up here.',
    to: '/create/templates/default/register-repository',
    icon: PlaylistAddIcon,
  },
  {
    title: 'Set up your coding agent',
    text: 'Get a CLAUDE.md / AGENTS.md and the shared Komax agent skills for your repo.',
    to: '/create/templates/default/coding-agent-setup',
    icon: SmartToyIcon,
  },
  {
    title: 'Read the guide',
    text: 'What the portal does, how teams and ownership work, and how onboarding works in detail.',
    to: '/docs/default/component/developer-portal',
    icon: LibraryBooksIcon,
  },
];

const tools = [
  {
    url: 'https://code.komaxgroup.com/ch10',
    label: 'GitLab ch10',
    icon: <CodeIcon />,
  },
  {
    url: 'https://dev.azure.com/Komax-TeamPhoenix',
    label: 'ADO TeamPhoenix',
    icon: <CodeIcon />,
  },
  {
    url: 'https://dev.azure.com/Komax-MFP',
    label: 'ADO MFP',
    icon: <CodeIcon />,
  },
  {
    url: 'https://dev.azure.com/Schleuniger-GS',
    label: 'ADO Schleuniger',
    icon: <CodeIcon />,
  },
  {
    url: 'https://ch10.code.komaxgroup.io/plant-data-management/',
    label: 'KDM docs',
    icon: <LibraryBooksIcon />,
  },
];

export const HomePage = () => {
  const classes = useStyles();

  return (
    <Page themeId="home">
      <Header
        title="Komax Developer Portal"
        subtitle="Who owns what, where its docs live, and how to build on it"
      />
      <Content>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <div className={classes.searchBar}>
              <HomePageSearchBar placeholder="Search services, APIs, docs and people" />
            </div>
          </Grid>

          {steps.map((step, index) => (
            <Grid item xs={12} sm={6} md={3} key={step.to}>
              <Card className={classes.step}>
                <CardActionArea
                  component={Link}
                  to={step.to}
                  className={classes.stepArea}
                >
                  <CardContent>
                    <step.icon className={classes.stepIcon} />
                    <Typography variant="overline" className={classes.stepNumber}>
                      Step {index + 1}
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {step.text}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}

          <Grid item xs={12} md={6}>
            <InfoCard title="Why this portal?">
              <ul className={classes.why}>
                <li>
                  <strong>One place for everything we build.</strong> Services
                  from GitLab and every Azure DevOps organization, with their
                  APIs, dependencies and owning team.
                </li>
                <li>
                  <strong>Answers without asking around.</strong> Who owns this
                  service? Where are its docs? Which API does it provide, and
                  who uses it?
                </li>
                <li>
                  <strong>A paved road for new work.</strong> Templates register
                  your repository and set up your coding agent with the shared
                  Komax skills.
                </li>
              </ul>
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <HomePageStarredEntities />
          </Grid>
          <Grid item xs={12}>
            <HomePageToolkit tools={tools} />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
